import React, { useRef, useState } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFile from "@mui/icons-material/UploadFile";
import { Box, Button, CircularProgress, LinearProgress } from "@mui/material";
import PaymentAPI from "api/faber/PaymentAPI";

import UploadPriceResponseDialog from "./UploadPriceResponseDialog";


interface CatalogPriceTabProps {
    faberToken: string;
}


const CatalogPriceTab: React.FC<CatalogPriceTabProps> = ({
    faberToken
}) => {
    const uploadInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
    const [responseDialogOpen, setResponseDialogOpen] = useState(false);
    const [uploadResponse, setUploadResponse] = useState({
        data: { successfulInsertions: [], failedInsertions: [] },
    });

    const handleDownloadCSV = () => {
        const header = "catalog_id,currency,list_price,interval,unit_amount\n";
        const csvRows = [
            "1156668680863088641,USD,16.00,0,20.00",
            "1156668680863088641,EUR,15.50,0,19.00",
            "1156668680863088641,GBP,13.50,0,16.50",
            "1156668680863088641,INR,1440.00,0,1800.00",
            "1156669400529174529,USD,9.00,0,12.00",
            "1156669400529174529,SGD,12.00,0,15.00",
            "1156669400529174529,CAD,11.50,0,14.50",
            "1156669400529174529,AUD,13.00,0,16.00"
        ];

        const blob = new Blob([header + csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "catalog_prices_template.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleUploadCSV = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target?.result as string;
            const rowCount = text.trim().split(/\r?\n/).length;
            if (rowCount >= 60) {
                alert("Please upload a CSV file with fewer than 60 rows.");
                return;
            }

            setUploadedFileName(file.name);
            setUploading(true);

            const formData = new FormData();
            formData.append("file", file);

            PaymentAPI.uploadFilePriceCSV(faberToken, formData)
                .then((response) => {
                    setUploadResponse(response.data);
                    setResponseDialogOpen(true);
                })
                .catch(() => {
                    alert("Error uploading file. Please try again.");
                    setUploadResponse({ data: { successfulInsertions: [], failedInsertions: [] } });
                    setResponseDialogOpen(true);
                })
                .finally(() => setUploading(false));
        };

        reader.readAsText(file);
    };

    const handleUploadButtonClick = () => uploadInputRef.current?.click();

    return (
        <Box>
            <Box
                display="flex"
                gap={4}
                mb={3}
                sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "#fafafa",
                    boxShadow: "0px 1px 4px rgba(0,0,0,0.1)",
                }}
            >
                <input
                    type="file"
                    ref={uploadInputRef}
                    style={{ display: "none" }}
                    onChange={handleUploadCSV}
                    accept=".csv"
                />
                <Box display="flex" flexDirection="column" gap={1}>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleDownloadCSV}
                        disabled={uploading}
                        sx={{ minWidth: 180 }}
                    >
                        Download CSV
                        <DownloadIcon sx={{ ml: 1 }} />
                    </Button>
                    <span style={{ fontSize: "0.8rem", color: "#666" }}>
                        📥 Get a template for catalog prices.
                    </span>
                </Box>

                <Box display="flex" flexDirection="column" gap={1}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleUploadButtonClick}
                        disabled={uploading}
                        sx={{ minWidth: 180, display: "flex", alignItems: "center", gap: 1 }}
                    >
                        {uploading ? "Uploading..." : "Upload CSV"}
                        {uploading ? (
                            <CircularProgress size={20} sx={{ color: "white" }} />
                        ) : (
                            <UploadFile />
                        )}
                    </Button>
                    <span style={{ fontSize: "0.8rem", color: "#666" }}>
                        📌 Must contain fewer than <strong style={{ color: "red" }}>60</strong> elements.
                    </span>
                    {uploadedFileName && (
                        <span style={{ fontSize: "0.8rem", color: "green" }}>
                            ✅ Uploaded file: <strong>{uploadedFileName}</strong>
                        </span>
                    )}
                </Box>
            </Box>

            {uploading && (
                <Box mb={2}>
                    <LinearProgress />
                </Box>
            )}

            <UploadPriceResponseDialog
                open={responseDialogOpen}
                onClose={() => setResponseDialogOpen(false)}
                responseData={uploadResponse}
            />
        </Box>
    );
};

export default CatalogPriceTab;
